import { getGetUsersUrl } from 'generatedTypes/user/services/user-controller';
import { useState } from 'react';

import useFetch from 'hooks/useFetch';
import usePagination from 'hooks/usePagination';
import useRetry from 'hooks/useRetry';

import useTranslation from './hooks/useTranslation';

import { DEFAULT_PAGE_SIZE, PILOT_ROLE } from './constants';
import type {
    FetchPilots,
    FetchPilotsParams,
    FetchUsers,
    FetchUsersParams,
    OrganizationMemberResponse,
    PagedResponseOrganizationMemberResponse
} from './types';

const useUsers = () => {
    const [isLoading, setIsLoading] = useState(false);
    const { authenticatedGet } = useFetch();
    const { withRetry } = useRetry();
    const { CONNECTION_ERROR } = useTranslation();

    const fetchUsers: FetchUsers = async request => {
        try {
            setIsLoading(true);
            const url = getGetUsersUrl(request);

            return await withRetry(
                () => authenticatedGet<PagedResponseOrganizationMemberResponse>(url),
                CONNECTION_ERROR,
                () => fetchUsers(request)
            );
        } finally {
            setIsLoading(false);
        }
    };

    const {
        data: usersData,
        fetch: paginatedFetchUsers,
        reset: resetUsers
    } = usePagination<OrganizationMemberResponse>({
        callback: fetchUsers
    });

    const handleFetchUsers = (args?: FetchUsersParams) => {
        const { name, pageSize = DEFAULT_PAGE_SIZE, roles, sort } = args || {};

        return paginatedFetchUsers({
            name,
            pageSize,
            roles,
            sort
        });
    };

    const fetchPilots: FetchPilots = async params => {
        const { name, pageSize, pageToken, sort } = params;

        return await fetchUsers({
            name,
            pageSize,
            pageToken,
            roles: [PILOT_ROLE],
            sort
        });
    };

    const {
        data: pilotsData,
        fetch: paginatedFetchPilots,
        reset: resetPilots
    } = usePagination<OrganizationMemberResponse>({
        callback: fetchPilots
    });

    const handleFetchPilots = (args?: FetchPilotsParams) => {
        const { name, pageSize = DEFAULT_PAGE_SIZE, sort } = args || {};

        return paginatedFetchPilots({
            name,
            pageSize,
            sort
        });
    };

    return {
        fetchPilots: handleFetchPilots,
        fetchUsers: handleFetchUsers,
        isLoading,
        pilots: pilotsData as OrganizationMemberResponse[],
        resetPilots,
        resetUsers,
        users: usersData as OrganizationMemberResponse[]
    };
};

export default useUsers;
